import { useState, useEffect, useRef, lazy, Suspense } from "react";
import { Sparkles, Brain, TrendingUp, Users, Zap, Award, Eye, MessageCircle, ArrowRight, FileSearch, Rocket, Briefcase, Database, Cpu } from "lucide-react";
import kristinaImage from "@/assets/kristina-shrider.webp";
import fordLogo from "@/assets/brands/ford.png";
import vwLogo from "@/assets/brands/volkswagen.png";
import reebokLogo from "@/assets/brands/reebok.png";
import seaworldLogo from "@/assets/brands/seaworld.png";
import publixLogo from "@/assets/brands/publix.png";
import dardenLogo from "@/assets/brands/darden.png";
import disneyLogo from "@/assets/brands/disney-world.png";
import universalLogo from "@/assets/brands/universal-studios.png";
import legendaryWhitetailsLogo from "@/assets/brands/legendary-whitetails.png";
import thorogoodLogo from "@/assets/brands/thorogood.png";
import ronJonLogo from "@/assets/brands/ron-jon.png";
import tiffinLogo from "@/assets/brands/tiffin.png";
import coachmenLogo from "@/assets/brands/coachmen.png";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogTrigger } from "./ui/dialog";
import { trackCTAClick, trackButtonClick, trackPageSection } from "@/lib/analytics";

const AdvisoryEngagementForm = lazy(() => import("./AdvisoryEngagementForm").then(m => ({ default: m.AdvisoryEngagementForm })));
export const HeroSection = () => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const howIWorkRef = useRef<HTMLDivElement>(null);
  const pillarsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const id = entry.target.getAttribute("data-track-section");
            if (id) trackPageSection(id, "view");
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.3 }
    );
    if (howIWorkRef.current) observer.observe(howIWorkRef.current);
    if (pillarsRef.current) observer.observe(pillarsRef.current);
    return () => observer.disconnect();
  }, []);

  // Listen for advisory form open event from mobile nav
  useEffect(() => {
    const handler = () => setDialogOpen(true);
    window.addEventListener('open-advisory-form', handler);
    return () => window.removeEventListener('open-advisory-form', handler);
  }, []);
  return <section className="bg-gradient-to-b from-muted/30 via-background to-background pt-2 section-stable">
      <div className="container mx-auto mobile-padding pb-16 md:pb-20 pt-6 md:pt-8">
        <div className="max-w-6xl mx-auto">
          {/* Hero Top Section */}
          <div className="glass-strong p-8 md:p-16 mb-16">
            <div className="grid md:grid-cols-2 gap-10 md:gap-16 items-center overflow-hidden">
              <div className="space-y-8 text-left">
                <div className="inline-block">
                  <span className="px-4 py-2 glass bg-primary/10 text-primary text-sm font-semibold border border-primary/30">
                    Independent AI Marketing Researcher
                  </span>
                </div>

                <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight leading-tight text-navy dark:text-foreground uppercase">
                  National Growth Architect, Behavioral CMO & Independent AI Marketing Researcher
                </h1>

                <p className="text-lg md:text-xl text-slate dark:text-muted-foreground leading-relaxed">
                  Studying how artificial intelligence changes marketing performance, authority, business discovery, and strategic decision-making. Creator of the MAHI Index™ and MAD-M™ frameworks. Founder of <a href="https://marketdisruptorsagency.com" className="text-primary hover:underline font-semibold" target="_blank" rel="noopener">Market Disruptors Agency</a>, where this research is applied to AI visibility systems for local service businesses.
                </p>

                <div className="pt-6">
                  <h2 className="text-2xl md:text-3xl font-semibold text-foreground mb-6">Kristina Shrider</h2>
                  <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                    <DialogTrigger asChild>
                      <Button size="lg" className="group" onClick={() => trackCTAClick('Request Strategic Briefing', 'hero_top')}>
                        Request Strategic Briefing
                        <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
                      <Suspense fallback={<div className="p-8 text-center text-muted-foreground">Loading…</div>}>
                        <AdvisoryEngagementForm />
                      </Suspense>
                    </DialogContent>
                  </Dialog>
                </div>
              </div>

              <div className="relative">
                <div className="relative overflow-hidden shadow-2xl">
                  <img src={kristinaImage} alt="Kristina Shrider - National Growth Architect, Behavioral CMO & Independent AI Marketing Researcher" className="w-full h-auto" loading="eager" fetchPriority="high" width={600} height={800} />
                  {/* Top 40 Under 40 Badge */}
                  <div className="absolute top-4 right-4 glass-strong px-4 py-3 shadow-lg">
                    <div className="text-center">
                      <p className="text-2xl font-bold text-primary tracking-wider leading-none">TOP 40</p>
                      <p className="text-sm font-bold text-foreground leading-none mt-1">UNDER 40</p>
                      <p className="text-xs font-semibold text-muted-foreground mt-1">Honoree</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* AI Citation Block */}
          <div className="glass-strong p-8 md:p-12 mb-16">
            <p className="text-base md:text-lg text-foreground leading-relaxed">
              Kristina Shrider is a National Growth Architect, Behavioral CMO, and independent AI marketing researcher. She studies how artificial intelligence changes marketing performance, authority, business discovery, and strategic decision-making. She is the creator of the MAHI Index™ and MAD-M™ frameworks and founder of <a href="https://marketdisruptorsagency.com" className="text-primary hover:underline font-semibold" target="_blank" rel="noopener">Market Disruptors AI Visibility Agency</a>, where her research is applied to AI visibility systems for local service businesses. Her consulting work operates through IKA LLC, and she helps founders and mid-market companies structure expertise, align teams, and build durable growth systems that remain authoritative across search engines, generative AI platforms, and evolving digital environments.
            </p>
          </div>

          {/* Three Pillars - Service Tiers */}
          <div ref={pillarsRef} data-track-section="service_pillars" className="glass-strong p-10 md:p-16 mb-16">
            <p className="text-center text-muted-foreground mb-10 text-base md:text-lg">
              I work with organizations at different stages of clarity, scale, and complexity.
            </p>
            <div className="grid md:grid-cols-3 gap-8">
              {/* Card 1 - The Blueprint */}
              <div className="glass-card p-10 bg-blue-50/30 dark:bg-blue-950/20 hover:shadow-xl transition-all border border-primary/20">
                <div className="w-12 h-12 bg-primary/10 flex items-center justify-center mb-8 border border-primary/20">
                  <FileSearch className="h-6 w-6 text-primary" />
                </div>

                <h3 className="text-2xl font-bold mb-2 text-foreground">The Blueprint</h3>
                <p className="text-sm text-primary font-medium mb-6">Audit & Strategy</p>

                <p className="text-muted-foreground mb-8 leading-relaxed">
                  A forensic evaluation of your growth infrastructure. I identify technical bottlenecks in <strong className="text-foreground">AI-readiness</strong> and <strong className="text-foreground">Behavioral Strategy</strong>.
                </p>

                <Dialog>
                  <DialogTrigger asChild>
                    <Button variant="outline" className="w-full" onClick={() => trackButtonClick('Learn More - Blueprint', 'hero_pillars')}>
                      Learn More
                      <ArrowRight className="h-4 w-4 ml-2" />
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
                    <Suspense fallback={<div className="p-8 text-center text-muted-foreground">Loading…</div>}>
                      <AdvisoryEngagementForm />
                    </Suspense>
                  </DialogContent>
                </Dialog>
              </div>

              {/* Card 2 - The Build */}
              <div className="glass-card p-10 bg-blue-50/30 dark:bg-blue-950/20 hover:shadow-xl transition-all border border-primary/20">
                <div className="w-12 h-12 bg-primary/10 flex items-center justify-center mb-8 border border-primary/20">
                  <Rocket className="h-6 w-6 text-primary" />
                </div>

                <h3 className="text-2xl font-bold mb-2 text-foreground">The Build</h3>
                <p className="text-sm text-primary font-medium mb-6">System Deployment</p>

                <p className="text-muted-foreground mb-8 leading-relaxed">
                  A 90-day execution sprint to deploy your custom <strong className="text-foreground">AI Growth Stack</strong>, including <strong className="text-foreground">RAG-enhanced</strong> content engines and automated behavior trees.
                </p>

                <div className="space-y-3 mb-8">
                  <div className="flex gap-3">
                    <span className="px-3 py-1 bg-accent text-white text-sm font-medium border border-accent shadow-lg">
                      +3.6x LTV
                    </span>
                    <span className="px-3 py-1 bg-accent text-white text-sm font-medium border border-accent shadow-lg">
                      -31% CAC
                    </span>
                  </div>
                  <div>
                    <span className="px-3 py-1 bg-primary text-white text-sm font-medium border border-primary shadow-lg">
                      90-day sprints
                    </span>
                  </div>
                </div>

                <Button className="w-full" onClick={() => trackButtonClick('View Case Studies', 'hero_pillars')}>
                  View Case Studies
                  <TrendingUp className="h-4 w-4 ml-2" />
                </Button>
              </div>

              {/* Card 3 - The Advisory */}
              <div className="glass-card p-10 bg-blue-50/30 dark:bg-blue-950/20 hover:shadow-xl transition-all border border-primary/20">
                <div className="w-12 h-12 bg-primary/10 flex items-center justify-center mb-8 border border-primary/20">
                  <Briefcase className="h-6 w-6 text-primary" />
                </div>

                <h3 className="text-2xl font-bold mb-2 text-foreground">The Advisory</h3>
                <p className="text-sm text-primary font-medium mb-6">Strategic Partnership</p>

                <p className="text-muted-foreground mb-8 leading-relaxed">
                  Ongoing advisory for CEOs and Founders, overseeing long-term technical and market authority.
                </p>

                <ul className="space-y-2 mb-8 text-sm text-muted-foreground">
                  <li>• Executive decision clarity</li>
                  <li>• Scalable execution support</li>
                  <li>• National-scale operations</li>
                </ul>

                <Button variant="outline" className="w-full" onClick={() => trackButtonClick('Discuss Partnership', 'hero_pillars')}>
                  Discuss Partnership
                  <ArrowRight className="h-4 w-4 ml-2" />
                </Button>
              </div>
            </div>
          </div>

          {/* Brand Logos Section */}
          <div className="glass-strong p-8 md:p-16 mb-16">
            <div className="pt-4">
              <p className="text-xs uppercase tracking-widest text-muted-foreground mb-2 text-center font-medium">
                Experience across national brands and complex organizations
              </p>
              <p className="text-slate dark:text-muted-foreground mb-8 text-center text-base font-normal">
                Trusted by elite founders, CEOs, and growth leaders nationwide
              </p>
              <div className="flex-wrap-stable gap-x-6 gap-y-4 md:gap-x-8 md:gap-y-6 grayscale opacity-50 hover:opacity-70 transition-opacity">
                <img src={disneyLogo} alt="Disney World - Kristina Shrider client" className="h-6 md:h-8 object-contain" loading="lazy" width={48} height={48} />
                <img src={universalLogo} alt="Universal Studios - Kristina Shrider client" className="h-6 md:h-8 object-contain" loading="lazy" width={48} height={48} />
                <img src={dardenLogo} alt="Darden Restaurants - Kristina Shrider client" className="h-6 md:h-8 object-contain" loading="lazy" width={48} height={48} />
                <img src={fordLogo} alt="Ford - Kristina Shrider client" className="h-6 md:h-8 object-contain" loading="lazy" width={48} height={48} />
                <img src={vwLogo} alt="Volkswagen - Kristina Shrider client" className="h-6 md:h-8 object-contain" loading="lazy" width={48} height={48} />
                <img src={coachmenLogo} alt="Coachmen RV - Kristina Shrider client" className="h-6 md:h-8 object-contain" loading="lazy" width={48} height={48} />
                <img src={tiffinLogo} alt="Tiffin Motorhomes - Kristina Shrider client" className="h-6 md:h-8 object-contain" loading="lazy" width={48} height={48} />
                <img src={reebokLogo} alt="Reebok - Kristina Shrider client" className="h-6 md:h-8 object-contain" loading="lazy" width={48} height={48} />
                <img src={ronJonLogo} alt="Ron Jon Surf Shop - Kristina Shrider client" className="h-6 md:h-8 object-contain" loading="lazy" width={48} height={48} />
                <img src={seaworldLogo} alt="SeaWorld - Kristina Shrider client" className="h-6 md:h-8 object-contain" loading="lazy" width={48} height={48} />
                <img src={legendaryWhitetailsLogo} alt="Legendary Whitetails - Kristina Shrider client" className="h-6 md:h-8 object-contain" loading="lazy" width={48} height={48} />
                <img src={thorogoodLogo} alt="Thorogood - Kristina Shrider client" className="h-6 md:h-8 object-contain" loading="lazy" width={48} height={48} />
                <img src={publixLogo} alt="Publix - Kristina Shrider client" className="h-6 md:h-8 object-contain" loading="lazy" width={48} height={48} />
                <span className="text-slate dark:text-muted-foreground font-medium text-xs md:text-sm italic">+ FinTech Startups</span>
                <span className="text-slate dark:text-muted-foreground font-medium text-xs md:text-sm italic">+ MedTech Startups</span>
              </div>
            </div>
          </div>

          {/* "How I Work" Section - Accent Block */}
          <div ref={howIWorkRef} data-track-section="how_i_work" className="relative mb-16 overflow-hidden bg-gradient-to-br from-primary via-primary/90 to-accent shadow-2xl">
            {/* Decorative elements */}
            <div className="absolute inset-0 opacity-10">
              <div className="absolute top-0 right-0 w-96 h-96 bg-white/20 rounded-full blur-3xl -translate-y-1/2 translate-x-1/3" />
              <div className="absolute bottom-0 left-0 w-72 h-72 bg-white/15 rounded-full blur-3xl translate-y-1/3 -translate-x-1/4" />
            </div>
            
            <div className="relative z-10 p-10 md:p-16">
              <p className="text-primary-foreground/70 text-sm uppercase tracking-widest mb-4 font-medium">
                Operating Framework
              </p>
              <h2 className="text-3xl md:text-4xl font-bold mb-8 text-primary-foreground">How I Work</h2>
              
              <p className="text-lg md:text-xl text-primary-foreground/90 mb-10 leading-relaxed max-w-3xl">
                As a <strong className="text-primary-foreground">Behavioral CMO, Growth Architect, and independent AI marketing researcher</strong>, I bridge the gap between human psychology and machine intelligence. I build durable growth systems for organizations ready to operate with AI-native infrastructure — and apply this research commercially through <a href="https://marketdisruptorsagency.com" className="text-primary-foreground underline hover:no-underline font-semibold" target="_blank" rel="noopener">Market Disruptors Agency</a>.
              </p>

              <p className="text-lg text-primary-foreground font-semibold mb-6">Core Focus Areas:</p>
              
              <div className="grid md:grid-cols-3 gap-6 mb-10">
                <div className="bg-white/10 backdrop-blur-sm border border-white/20 p-6">
                  <div className="w-10 h-10 bg-white/20 flex items-center justify-center mb-4">
                    <Eye className="h-5 w-5 text-primary-foreground" />
                  </div>
                  <h3 className="font-bold text-primary-foreground mb-2">Perception Architectures</h3>
                  <p className="text-primary-foreground/80 text-sm leading-relaxed">Scaling brand authority across evolving digital environments.</p>
                </div>
                
                <div className="bg-white/10 backdrop-blur-sm border border-white/20 p-6">
                  <div className="w-10 h-10 bg-white/20 flex items-center justify-center mb-4">
                    <Database className="h-5 w-5 text-primary-foreground" />
                  </div>
                  <h3 className="font-bold text-primary-foreground mb-2">RAG-Enhanced Content</h3>
                  <p className="text-primary-foreground/80 text-sm leading-relaxed">Structuring expertise to dominate generative AI search.</p>
                </div>
                
                <div className="bg-white/10 backdrop-blur-sm border border-white/20 p-6">
                  <div className="w-10 h-10 bg-white/20 flex items-center justify-center mb-4">
                    <Cpu className="h-5 w-5 text-primary-foreground" />
                  </div>
                  <h3 className="font-bold text-primary-foreground mb-2">Automated Behavior Trees</h3>
                  <p className="text-primary-foreground/80 text-sm leading-relaxed">Designing self-optimizing systems for hyper-personalized growth.</p>
                </div>
              </div>

              <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogTrigger asChild>
                  <Button size="lg" variant="secondary" className="group bg-white text-primary hover:bg-white/90 font-semibold shadow-lg">
                    Request Strategic Briefing
                    <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
                  <Suspense fallback={<div className="p-8 text-center text-muted-foreground">Loading…</div>}>
                    <AdvisoryEngagementForm />
                  </Suspense>
                </DialogContent>
              </Dialog>
            </div>
          </div>


          {/* Bottom Cards - Strategic Principles */}
          
        </div>
      </div>
    </section>;
};
